/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {coerceBooleanProperty} from '@angular/cdk/coercion'
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    Directive,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core'
import {merge, of as observableOf, Subscription} from 'rxjs'
import {matRangeDatepicker} from './datepicker'
import {matRangeDatepickerIntl} from './datepicker-intl'


/** Can be used to override the icon of a `matDatepickerToggle`. */
@Directive({
    selector: '[matDatepickerToggleIcon]',
})
export class matRangeDatepickerToggleIcon {
}


@Component({
    moduleId: module.id,
    selector: 'mat-range-datepicker-toggle',
    template: `<button mat-icon-button type="button" [attr.aria-label]="_intl.openCalendarLabel"
        [disabled]="disabled" (click)="_open($event)">

    <svg
            *ngIf="!_customIcon"
            class="mat-datepicker-toggle-default-icon"
            viewBox="0 0 24 24"
            width="24px"
            height="24px"
            fill="currentColor"
            focusable="false">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
    </svg>

    <ng-content select="[matDatepickerToggleIcon]"></ng-content>
</button>
`,
    styles: [`.mat-form-field-appearance-legacy .mat-form-field-prefix .mat-datepicker-toggle-default-icon,.mat-form-field-appearance-legacy .mat-form-field-suffix .mat-datepicker-toggle-default-icon{width:1em}.mat-form-field:not(.mat-form-field-appearance-legacy) .mat-form-field-prefix .mat-datepicker-toggle-default-icon,.mat-form-field:not(.mat-form-field-appearance-legacy) .mat-form-field-suffix .mat-datepicker-toggle-default-icon{display:block;width:1.5em;height:1.5em}.mat-form-field:not(.mat-form-field-appearance-legacy) .mat-form-field-prefix .mat-icon-button .mat-datepicker-toggle-default-icon,.mat-form-field:not(.mat-form-field-appearance-legacy) .mat-form-field-suffix .mat-icon-button .mat-datepicker-toggle-default-icon{margin:auto}`],
    host: {
        'class': 'mat-datepicker-toggle',
        '[class.mat-datepicker-toggle-active]': 'datepicker && datepicker.opened',
    },
    exportAs: 'matDatepickerToggle',
    encapsulation: ViewEncapsulation.None,
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class matRangeDatepickerToggle<D> implements AfterContentInit, OnChanges, OnDestroy {
    /** Datepicker instance that the button will toggle. */
    @Input('for') datepicker: matRangeDatepicker<D>

    /** Custom icon set by the consumer. */
    @ContentChild(matRangeDatepickerToggleIcon) _customIcon: matRangeDatepickerToggleIcon

    private _stateChanges = Subscription.EMPTY

    private _disabled: boolean

    constructor(public _intl: matRangeDatepickerIntl, private _changeDetectorRef: ChangeDetectorRef) {
    }

    /** Whether the toggle button is disabled. */
    @Input()
    get disabled(): boolean {
        return this._disabled === undefined ? this.datepicker.disabled : !!this._disabled
    }

    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value)
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.datepicker) {
            this._watchStateChanges()
        }
    }

    ngOnDestroy() {
        this._stateChanges.unsubscribe()
    }

    ngAfterContentInit() {
        this._watchStateChanges()
    }

    _open(event: Event): void {
        if (this.datepicker && !this.disabled) {
            this.datepicker.open()
            event.stopPropagation()
        }
    }

    private _watchStateChanges() {
        const datepickerDisabled = this.datepicker ? this.datepicker._disabledChange : observableOf()
        const inputDisabled = this.datepicker && this.datepicker._datepickerInput ?
            this.datepicker._datepickerInput._disabledChange : observableOf()
        const datepickerToggled = this.datepicker ?
            merge(this.datepicker.openedStream, this.datepicker.closedStream) :
            observableOf()

        this._stateChanges.unsubscribe()
        this._stateChanges = merge(
            this._intl.changes,
            datepickerDisabled,
            inputDisabled,
            datepickerToggled,
        ).subscribe(() => this._changeDetectorRef.markForCheck())
    }
}
